/**
 * Seasons Context
 * Provides global state for the gamification seasons system
 */

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  ReactNode,
} from 'react';
import { seasonsService } from '@flowstate/core';
import type {
  SeasonWithTheme,
  UserSeasonStats,
  SeasonLeaderboardEntry,
  SeasonTimeRemaining,
} from '@flowstate/types';
import { useAuth } from '@/hooks/useAuth';
import { logger } from "@/lib/logger";

interface SeasonsContextValue {
  // Active season
  activeSeason: SeasonWithTheme | null;
  isLoadingSeason: boolean;
  
  // User stats
  userStats: UserSeasonStats | null;
  isLoadingUserStats: boolean;
  
  // Leaderboard
  seasonLeaderboard: SeasonLeaderboardEntry[];
  isLoadingLeaderboard: boolean;
  
  // Time remaining
  timeRemaining: SeasonTimeRemaining | null;
  
  // Actions
  refreshActiveSeason: () => Promise<void>;
  refreshUserStats: () => Promise<void>;
  refreshLeaderboard: (limit?: number, offset?: number) => Promise<void>;
  
  // Helpers
  isSeasonActive: boolean;
  hasActiveSeason: boolean;
}

const SeasonsContext = createContext<SeasonsContextValue | undefined>(
  undefined
);

interface SeasonsProviderProps {
  children: ReactNode;
}

export const SeasonsProvider: React.FC<SeasonsProviderProps> = ({
  children,
}) => {
  const { user } = useAuth();
  
  // State
  const [activeSeason, setActiveSeason] = useState<SeasonWithTheme | null>(null);
  const [isLoadingSeason, setIsLoadingSeason] = useState(true);
  
  const [userStats, setUserStats] = useState<UserSeasonStats | null>(null);
  const [isLoadingUserStats, setIsLoadingUserStats] = useState(false);
  
  const [seasonLeaderboard, setSeasonLeaderboard] = useState<SeasonLeaderboardEntry[]>([]);
  const [isLoadingLeaderboard, setIsLoadingLeaderboard] = useState(false);
  
  const [timeRemaining, setTimeRemaining] = useState<SeasonTimeRemaining | null>(null);

  // Refresh active season
  const refreshActiveSeason = useCallback(async () => {
    setIsLoadingSeason(true);
    try {
      const response = await seasonsService.getActiveSeason();
      const season = response.data ?? null;
      setActiveSeason(season);
      
      // Calculate time remaining if season exists
      if (season) {
        const remaining = seasonsService.calculateTimeRemaining(season.end_date);
        setTimeRemaining(remaining);
      } else {
        setTimeRemaining(null);
      }
    } catch (error) {
      logger.error('Error refreshing active season:', { error: error });
    } finally {
      setIsLoadingSeason(false);
    }
  }, []);

  // Refresh user stats
  const refreshUserStats = useCallback(async () => {
    if (!user?.id) {
      setUserStats(null);
      return;
    }
    
    setIsLoadingUserStats(true);
    try {
      const response = await seasonsService.getUserSeasonStats(user.id);
      setUserStats(response.data ?? null);
    } catch (error) {
      logger.error('Error refreshing user stats:', { error: error });
    } finally {
      setIsLoadingUserStats(false);
    }
  }, [user?.id]);

  // Refresh leaderboard
  const refreshLeaderboard = useCallback(async (limit = 10, _offset = 0) => {
    setIsLoadingLeaderboard(true);
    try {
      if (!activeSeason?.id) {
        setSeasonLeaderboard([]);
        return;
      }
      const response = await seasonsService.getSeasonLeaderboard(
        activeSeason.id,
        limit
      );
      // Ensure we always set an array, even if response is invalid
      const leaderboard = Array.isArray(response?.data) ? response.data : [];
      setSeasonLeaderboard(leaderboard);
    } catch (error) {
      logger.error('Error refreshing leaderboard:', { error: error });
      setSeasonLeaderboard([]); // Set empty array on error
    } finally {
      setIsLoadingLeaderboard(false);
    }
  }, [activeSeason?.id]);

  // Load active season on mount
  useEffect(() => {
    if (user?.id) {
      refreshActiveSeason();
    } else {
      setIsLoadingSeason(false);
    }
  }, [user?.id, refreshActiveSeason]);

  // Load user stats when user or season changes
  useEffect(() => {
    if (user && activeSeason) {
      refreshUserStats();
    }
  }, [user, activeSeason, refreshUserStats]);

  // Update time remaining every minute
  useEffect(() => {
    if (!activeSeason) {return;}

    const interval = setInterval(() => {
      const remaining = seasonsService.calculateTimeRemaining(activeSeason.end_date);
      setTimeRemaining(remaining);
      
      // If season has ended, refresh active season
      if (remaining.total_seconds <= 0) {
        refreshActiveSeason();
      }
    }, 60000); // Update every minute

    return () => clearInterval(interval);
  }, [activeSeason, refreshActiveSeason]);

  // Computed values
  const hasActiveSeason = activeSeason !== null;
  const isSeasonActive = hasActiveSeason && 
    seasonsService.isSeasonActive(activeSeason);

  const value: SeasonsContextValue = {
    // State
    activeSeason,
    isLoadingSeason,
    userStats,
    isLoadingUserStats,
    seasonLeaderboard,
    isLoadingLeaderboard,
    timeRemaining,
    
    // Actions
    refreshActiveSeason,
    refreshUserStats,
    refreshLeaderboard,
    
    // Helpers
    isSeasonActive,
    hasActiveSeason,
  };

  return (
    <SeasonsContext.Provider value={value}>
      {children}
    </SeasonsContext.Provider>
  );
};

/**
 * Hook to use the seasons context
 */
export const useSeasons = (): SeasonsContextValue => {
  const context = useContext(SeasonsContext);
  if (context === undefined) {
    throw new Error('useSeasons must be used within a SeasonsProvider');
  }
  return context;
};
